import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { AdminFormPage } from '../components/AdminFormPage'
import { adminSeasonsService } from '../services/admin.seasons.service'

const seasonSchema = z.object({
  number: z.number().int().min(1, 'Season number must be at least 1'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  year: z.number().int().min(2000, 'Year must be 2000 or later'),
  isPublished: z.boolean(),
  order: z.number().int().min(0, 'Order must be 0 or greater'),
})

export function AdminSeasonEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()

  const [loading, setLoading] = useState(!isNew)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof seasonSchema>>({
    resolver: zodResolver(seasonSchema),
    defaultValues: {
      number: 1,
      title: '',
      description: '',
      year: new Date().getFullYear(),
      isPublished: false,
      order: 0,
    },
  })

  useEffect(() => {
    if (isNew || !id) return
    setLoading(true)
    adminSeasonsService
      .getAllSeasons()
      .then((all) => {
        const found = all.find((s) => s.id === id)
        if (found) {
          reset({
            number: found.number,
            title: found.title,
            description: found.description,
            year: found.year,
            isPublished: found.isPublished,
            order: found.order,
          })
        }
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load season'))
      )
      .finally(() => setLoading(false))
  }, [id, isNew, reset])

  async function onSubmit(values: z.infer<typeof seasonSchema>) {
    setSubmitError(null)
    try {
      if (isNew) {
        await adminSeasonsService.createSeason(values)
      } else if (id) {
        await adminSeasonsService.updateSeason(id, values)
      }
      navigate('/admin/config/seasons')
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Failed to save season'))
    }
  }

  return (
    <AdminFormPage
      title={isNew ? 'New Season' : 'Edit Season'}
      cardTitle={isNew ? 'Create a new season' : 'Update season details'}
      backTo="/admin/config/seasons"
      loading={loading}
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="number">Season Number</Label>
            <Input
              id="number"
              type="number"
              min={1}
              {...register('number', { valueAsNumber: true })}
            />
            {errors.number && (
              <p className="text-sm text-destructive">{withErrorCode(String(errors.number.message), 'app/validation')}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="year">Year</Label>
            <Input
              id="year"
              type="number"
              min={2000}
              {...register('year', { valueAsNumber: true })}
            />
            {errors.year && (
              <p className="text-sm text-destructive">{withErrorCode(String(errors.year.message), 'app/validation')}</p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            type="text"
            placeholder="Season 1"
            {...register('title')}
          />
          {errors.title && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.title.message), 'app/validation')}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={4}
            placeholder="Describe this season…"
            {...register('description')}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.description.message), 'app/validation')}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="order">Sort Order</Label>
          <Input
            id="order"
            type="number"
            min={0}
            {...register('order', { valueAsNumber: true })}
          />
          {errors.order && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <input
            id="isPublished"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            {...register('isPublished')}
          />
          <Label htmlFor="isPublished">Published</Label>
        </div>

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isNew ? 'Create Season' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/config/seasons')}>
            Cancel
          </Button>
        </div>
      </form>
    </AdminFormPage>
  )
}
